import React, { useEffect, useMemo, useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Image as ImageIcon, Edit3, Trash2, Printer, Save, X, Gem, Search } from "lucide-react";
import { api, fileUrl, formatError } from "@/lib/api";
import { computePrice, fmtINR } from "@/lib/pricing";
import { useRates } from "@/context/RatesContext";

const KARATS = ["22kt", "24kt", "18kt", "14kt"];

export default function AdminInventory() {
  const { rates } = useRates();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(25);
  const [editing, setEditing] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null); // { phase, current, total, pct, label }
  const excelRef = useRef();
  const bulkRef = useRef();
  const folderRef = useRef();

  // Set webkitdirectory attribute via DOM for reliable folder picking (some
  // browsers ignore it via React props).
  useEffect(() => {
    if (folderRef.current) {
      folderRef.current.setAttribute("webkitdirectory", "");
      folderRef.current.setAttribute("directory", "");
      folderRef.current.setAttribute("mozdirectory", "");
    }
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/items", { params: { q: q || undefined, limit, offset } });
      setItems(data.items);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [q, offset]);

  const handleExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    toast("Importing Excel…");
    try {
      const { data } = await api.post("/admin/items/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported: ${data.created} new, ${data.updated} updated, ${data.skipped} skipped`);
      load();
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail) || "Import failed");
    }
    e.target.value = "";
  };

  const handleBulkImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const CHUNK_SIZE = 8;
    const chunks = [];
    for (let i = 0; i < files.length; i += CHUNK_SIZE) chunks.push(files.slice(i, i + CHUNK_SIZE));
    setUploadProgress({ phase: "uploading", pct: 0, total: files.length, label: `Uploading ${files.length} images…` });
    const tId = toast.loading(`Uploading ${files.length} images… 0%`);
    let totalMatched = 0;
    const allUnmatched = [];
    let completed = 0;
    try {
      for (const [idx, chunk] of chunks.entries()) {
        const fd = new FormData();
        chunk.forEach((f) => fd.append("files", f));
        try {
          const { data } = await api.post("/admin/items/bulk-image", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
          totalMatched += data.matched || 0;
          if (data.unmatched?.length) allUnmatched.push(...data.unmatched);
        } catch (err) {
          toast.error(`Batch ${idx + 1} failed: ${formatError(err?.response?.data?.detail) || err?.message || "Network error"}`);
        }
        completed += chunk.length;
        const pct = Math.round((completed * 100) / files.length);
        setUploadProgress({ phase: "uploading", pct, total: files.length, label: `Uploaded batch ${idx + 1} of ${chunks.length} — ${pct}%` });
        toast.loading(`Uploading ${files.length} images… ${pct}%`, { id: tId });
      }
      toast.success(`Matched ${totalMatched}/${files.length}${allUnmatched.length ? ` · ${allUnmatched.length} unmatched` : ""}`, { id: tId, duration: 6000 });
      load();
    } finally {
      setUploadProgress(null);
      e.target.value = "";
    }
  };

  /** Ringstudio folder upload
   *  Expects nested folder structure:
   *    /parent-folder/SKU-CODE/1.jpg    → tag SKU-CODE, slot 0
   *    /parent-folder/SKU-CODE/2.jpg    → slot 1, etc.
   *  webkitRelativePath gives us the full path; we rename each file to `SKU_N.ext`
   *  so the existing bulk-image endpoint can match it.
   */
  const ALLOWED_EXT = ["jpg", "jpeg", "png", "webp"];
  const SYSTEM_FILES = [".ds_store", "thumbs.db", "desktop.ini", ".gitkeep"];

  const handleRingstudioFolder = async (e) => {
    const allFiles = Array.from(e.target.files || []);
    if (!allFiles.length) {
      toast.error("No files received. Please select all images inside the folder using Ctrl+A or Cmd+A.");
      return;
    }
    const renamed = [];
    const skipped = [];
    const rejected = [];

    for (const f of allFiles) {
      const rel = f.webkitRelativePath || f.name;
      const parts = rel.split("/");
      const fileName = parts[parts.length - 1];
      const lower = fileName.toLowerCase();

      // Skip system / hidden files
      if (SYSTEM_FILES.includes(lower) || lower.startsWith(".") || lower.startsWith("~$")) {
        skipped.push(fileName);
        continue;
      }
      const ext = (lower.includes(".") ? lower.split(".").pop() : "");
      if (!ALLOWED_EXT.includes(ext)) {
        rejected.push(`${fileName} — unsupported type (.${ext || "?"})`);
        continue;
      }
      // Reject very large files (> 15 MB) before upload
      if (f.size > 15 * 1024 * 1024) {
        rejected.push(`${fileName} — file too large (${(f.size / 1024 / 1024).toFixed(1)} MB, max 15 MB)`);
        continue;
      }
      if (parts.length < 2) {
        // Single file without folder context — use stem as SKU
        const stem = fileName.replace(/\.[^.]+$/, "");
        const newFile = new File([f], `${stem}.${ext}`, { type: f.type || `image/${ext}` });
        renamed.push(newFile);
        continue;
      }
      const skuFolder = parts[parts.length - 2];
      const m = fileName.match(/(\d+)/);
      const slot = m ? Math.max(1, parseInt(m[1], 10)) : 1;
      const newName = `${skuFolder}_${slot}.${ext}`;
      const newFile = new File([f], newName, { type: f.type || `image/${ext}` });
      renamed.push(newFile);
    }

    if (!renamed.length) {
      const reasons = [];
      if (rejected.length) reasons.push(`${rejected.length} unsupported`);
      if (skipped.length) reasons.push(`${skipped.length} system files skipped`);
      toast.error(`No valid images found${reasons.length ? ` (${reasons.join(", ")})` : ""}. Allowed: JPG, PNG, WEBP.`);
      return;
    }

    if (rejected.length) {
      // Surface the first 3 specific rejections so the user knows what was wrong
      rejected.slice(0, 3).forEach((r) => toast.warning(r));
      if (rejected.length > 3) toast.warning(`…and ${rejected.length - 3} more file(s) rejected`);
    }

    const fd_total = renamed.length;
    setUploadProgress({ phase: "uploading", pct: 0, total: fd_total, label: `Preparing ${fd_total} ring images…` });
    const tId = toast.loading(`Uploading ${fd_total} ring images… 0%`);

    // CHUNK uploads to avoid Kubernetes ingress / proxy body-size limits and
    // to give granular per-batch error reporting.
    const CHUNK_SIZE = 8;
    const chunks = [];
    for (let i = 0; i < renamed.length; i += CHUNK_SIZE) chunks.push(renamed.slice(i, i + CHUNK_SIZE));

    let totalMatched = 0;
    const allUnmatched = [];
    const allErrors = [];
    let completed = 0;

    try {
      for (const [idx, chunk] of chunks.entries()) {
        const fd = new FormData();
        chunk.forEach((f) => fd.append("files", f));
        try {
          const { data } = await api.post("/admin/items/bulk-image", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 120000,
          });
          totalMatched += data.matched || 0;
          if (data.unmatched?.length) allUnmatched.push(...data.unmatched);
          if (data.errors?.length) allErrors.push(...data.errors);
        } catch (err) {
          // Fail one chunk → record but continue with others
          const status = err?.response?.status;
          const detail = formatError(err?.response?.data?.detail) || err?.message || "Network error";
          allErrors.push(...chunk.map((f) => ({ file: f.name, error: `${status ? status + " — " : ""}${detail}` })));
        }
        completed += chunk.length;
        const pct = Math.round((completed * 100) / fd_total);
        setUploadProgress({ phase: "uploading", pct, total: fd_total, label: `Uploaded batch ${idx + 1} of ${chunks.length} — ${pct}%` });
        toast.loading(`Uploading ${fd_total} ring images… ${pct}%`, { id: tId });
      }

      const failedCount = allErrors.length;
      if (failedCount === 0 && allUnmatched.length === 0) {
        toast.success(`Matched all ${totalMatched}/${fd_total} images`, { id: tId, duration: 6000 });
      } else if (totalMatched > 0) {
        toast.success(
          `Matched ${totalMatched}/${fd_total}${allUnmatched.length ? ` · ${allUnmatched.length} unmatched (no item with that SKU)` : ""}${failedCount ? ` · ${failedCount} upload errors` : ""}`,
          { id: tId, duration: 8000 }
        );
      } else {
        toast.error(`Upload failed — 0 of ${fd_total} matched. ${failedCount ? "Server errors on every batch." : "No items found with those SKUs."}`, { id: tId, duration: 10000 });
      }

      if (allUnmatched.length) {
        toast.warning(`Unmatched: ${allUnmatched.slice(0, 3).join(", ")}${allUnmatched.length > 3 ? `… (+${allUnmatched.length - 3} more)` : ""}`);
      }
      if (failedCount) {
        // Show specific server-side errors with file name
        allErrors.slice(0, 3).forEach((e) => toast.error(`${e.file}: ${e.error}`));
        if (failedCount > 3) toast.error(`…and ${failedCount - 3} more file errors`);
      }
      load();
    } finally {
      setUploadProgress(null);
      e.target.value = "";
    }
  };

  const handleSingleImage = async (item, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/items/${item.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image updated");
      load();
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail) || "Failed");
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete item ${item.tag_no}?`)) return;
    try {
      await api.delete(`/admin/items/${item.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail) || "Failed");
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const { id, karat, item_name, category, gold_weight, diamond_weight, size, colour, clarity, certification, hallmark, pieces } = editing;
    try {
      await api.put(`/admin/items/${id}`, { karat, item_name, category, gold_weight: Number(gold_weight) || 0, diamond_weight: Number(diamond_weight) || 0, size, colour, clarity, certification, hallmark, pieces: Number(pieces) || 0 });
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail) || "Failed");
    }
  };

  const printTag = (tag_no) => {
    window.open(`/print-tag/${encodeURIComponent(tag_no)}`, "_blank");
  };

  return (
    <div className="p-10 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="ornament mb-2">Inventory</div>
          <h1 className="font-display text-4xl text-ivory" style={{ fontWeight: 300 }}>Stock & Catalogue</h1>
          <p className="text-ivory/60 mt-1 text-sm">{total} pieces · Upload Excel, drop images by filename</p>
        </div>
        <div className="flex gap-2">
          <input ref={excelRef} type="file" accept=".xls,.xlsx" className="hidden" onChange={handleExcel} data-testid="excel-input" />
          <button onClick={() => excelRef.current?.click()} className="inline-flex items-center gap-2 border border-rose-gold/40 px-4 py-3 text-sm text-ivory hover:bg-rose-gold/10" data-testid="import-excel-button">
            <FileSpreadsheet className="w-4 h-4 text-rose-gold" /> Import Excel
          </button>
          <input ref={bulkRef} type="file" multiple accept="image/*" className="hidden" onChange={handleBulkImages} data-testid="bulk-images-input" />
          <button onClick={() => bulkRef.current?.click()} className="inline-flex items-center gap-2 bg-rose-gold text-ink-900 px-4 py-3 text-sm font-medium hover:bg-champagne" data-testid="bulk-images-button">
            <Upload className="w-4 h-4" /> Upload Images (by Tag No)
          </button>
          <input
            ref={folderRef}
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            onChange={handleRingstudioFolder}
            data-testid="ringstudio-folder-input"
          />
          <button onClick={() => folderRef.current?.click()} className="inline-flex items-center gap-2 border border-rose-gold/50 px-4 py-3 text-sm text-rose-gold hover:bg-rose-gold/10" data-testid="ringstudio-folder-button" title="Select a parent folder containing one sub-folder per ring SKU. Each sub-folder should hold 1.jpg, 2.jpg…">
            <Upload className="w-4 h-4" /> Ringstudio Folder
          </button>
        </div>
      </div>

      {/* Upload progress bar */}
      {uploadProgress && (
        <div className="border border-rose-gold/40 bg-ink-800/60 px-4 py-3 flex items-center gap-4" data-testid="upload-progress">
          <div className="flex-1">
            <div className="text-sm text-ivory mb-1.5">{uploadProgress.label}</div>
            <div className="w-full h-1.5 bg-white/10 overflow-hidden">
              <div className="h-full bg-rose-gold transition-all duration-300" style={{ width: `${uploadProgress.pct}%` }} />
            </div>
          </div>
          <div className="text-rose-gold font-display text-xl tabular-nums">{uploadProgress.pct}%</div>
        </div>
      )}

      {/* Folder upload helper hint */}
      <div className="text-[11px] text-ivory/50 leading-relaxed -mt-2">
        <span className="text-rose-gold/80">Ringstudio Folder:</span> select the parent folder — each sub-folder name should match a Ring p-SKU (e.g. <code className="text-rose-gold">C2CR000006</code>) and contain <code className="text-rose-gold">1.jpg, 2.jpg…</code>. Allowed: JPG, PNG, WEBP. System files (<code>.DS_Store</code>, <code>Thumbs.db</code>) are skipped automatically. If your browser blocks folder selection, open the folder and press <kbd className="px-1.5 py-0.5 border border-white/15 text-ivory/80">Ctrl/Cmd + A</kbd> to select all images instead.
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ivory/40" />
        <input value={q} onChange={(e) => { setOffset(0); setQ(e.target.value); }} placeholder="Search tag no or name…" className="w-full bg-ink-800 border border-white/5 pl-10 pr-4 py-3 text-sm text-ivory placeholder-ivory/30 focus:border-rose-gold" data-testid="inventory-search" />
      </div>

      <div className="border border-white/5 overflow-x-auto">
        <table className="w-full text-sm" data-testid="inventory-table">
          <thead className="bg-ink-800/80">
            <tr className="text-left text-ivory/50 uppercase text-[11px] tracking-widest">
              <th className="p-3">Image</th>
              <th className="p-3">Tag</th>
              <th className="p-3">Name</th>
              <th className="p-3">Category</th>
              <th className="p-3">Karat</th>
              <th className="p-3 text-right">Gold</th>
              <th className="p-3 text-right">Diamond</th>
              <th className="p-3 text-right">Live Price</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading && items.length === 0 ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={`inv-skel-${i}`}><td colSpan={9} className="p-4"><div className="h-6 shimmer" /></td></tr>
              ))
            ) : (
              items.map((it) => {
                const p = computePrice(it, rates, false);
                return (
                  <tr key={it.id} className="hover:bg-white/5" data-testid={`inventory-row-${it.tag_no}`}>
                    <td className="p-3">
                      <label className="block w-14 h-14 bg-ink-900 border border-white/10 cursor-pointer overflow-hidden relative group">
                        {it.image_path ? (
                          <img src={fileUrl(it.image_path)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Gem className="w-5 h-5 text-rose-gold/30 absolute inset-0 m-auto" />
                        )}
                        <div className="absolute inset-0 bg-ink-900/70 opacity-0 group-hover:opacity-100 flex items-center justify-center">
                          <ImageIcon className="w-4 h-4 text-rose-gold" />
                        </div>
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) handleSingleImage(it, e.target.files[0]); }} data-testid={`upload-image-${it.tag_no}`} />
                      </label>
                    </td>
                    <td className="p-3"><span className="overline text-rose-gold">{it.tag_no}</span></td>
                    <td className="p-3 text-ivory">{it.item_name}</td>
                    <td className="p-3 text-ivory/60">{it.category || "—"}</td>
                    <td className="p-3"><span className="px-2 py-0.5 border border-rose-gold/30 text-rose-gold text-xs">{it.karat}</span></td>
                    <td className="p-3 text-right text-ivory/80">{(it.gold_weight || 0).toFixed(3)}g</td>
                    <td className="p-3 text-right text-ivory/80">{(it.diamond_weight || 0).toFixed(3)}ct</td>
                    <td className="p-3 text-right font-display text-champagne">{fmtINR(p.final)}</td>
                    <td className="p-3">
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => setEditing({ ...it })} className="p-2 text-ivory/60 hover:text-rose-gold" title="Edit" data-testid={`edit-${it.tag_no}`}><Edit3 className="w-4 h-4" /></button>
                        <button onClick={() => printTag(it.tag_no)} className="p-2 text-ivory/60 hover:text-rose-gold" title="Print tag" data-testid={`print-${it.tag_no}`}><Printer className="w-4 h-4" /></button>
                        <button onClick={() => deleteItem(it)} className="p-2 text-ivory/60 hover:text-hallmark" title="Delete" data-testid={`delete-${it.tag_no}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center text-sm text-ivory/60">
        <span>{offset + 1}–{Math.min(offset + limit, total)} of {total}</span>
        <div className="flex gap-2">
          <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))} className="px-3 py-2 border border-white/10 disabled:opacity-30" data-testid="pagination-prev">Prev</button>
          <button disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)} className="px-3 py-2 border border-white/10 disabled:opacity-30" data-testid="pagination-next">Next</button>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-ink-800 border border-rose-gold/30 p-8 w-full max-w-2xl space-y-4 overflow-y-auto max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center">
              <h2 className="font-display text-2xl text-ivory">Edit {editing.tag_no}</h2>
              <button onClick={() => setEditing(null)} className="p-2"><X className="w-4 h-4 text-ivory/60" /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Item Name" value={editing.item_name} onChange={(v) => setEditing({ ...editing, item_name: v })} />
              <Field label="Category" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} />
              <div>
                <label className="overline text-ivory/50">Karat</label>
                <select value={editing.karat} onChange={(e) => setEditing({ ...editing, karat: e.target.value })} className="mt-1 w-full bg-ink-900 border border-white/10 px-4 py-3 text-ivory" data-testid="edit-karat">
                  {KARATS.map((k) => <option key={k}>{k}</option>)}
                </select>
              </div>
              <Field label="Gold Weight (g)" type="number" step="0.001" value={editing.gold_weight ?? 0} onChange={(v) => setEditing({ ...editing, gold_weight: v })} />
              <Field label="Diamond Weight (ct)" type="number" step="0.001" value={editing.diamond_weight ?? 0} onChange={(v) => setEditing({ ...editing, diamond_weight: v })} />
              <Field label="Size" value={editing.size || ""} onChange={(v) => setEditing({ ...editing, size: v })} />
              <Field label="Colour" value={editing.colour || ""} onChange={(v) => setEditing({ ...editing, colour: v })} />
              <Field label="Clarity" value={editing.clarity || ""} onChange={(v) => setEditing({ ...editing, clarity: v })} />
              <Field label="Certification" value={editing.certification || ""} onChange={(v) => setEditing({ ...editing, certification: v })} />
              <Field label="Hallmark" value={editing.hallmark || ""} onChange={(v) => setEditing({ ...editing, hallmark: v })} />
              <Field label="Pieces" type="number" value={editing.pieces ?? 1} onChange={(v) => setEditing({ ...editing, pieces: v })} />
            </div>
            <button onClick={saveEdit} className="w-full bg-rose-gold text-ink-900 py-3 font-medium tracking-widest uppercase text-sm hover:bg-champagne flex items-center justify-center gap-2" data-testid="edit-save">
              <Save className="w-4 h-4" /> Save Changes
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", step }) => (
  <div>
    <label className="overline text-ivory/50">{label}</label>
    <input type={type} step={step} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full bg-ink-900 border border-white/10 px-4 py-3 text-ivory focus:border-rose-gold" />
  </div>
);
